// Pure module (types only, no React or DOM): crew-mobile deep-imports it.

/** The viewer's row in a cohort chat. `left` is a former member: read-only up to `left_at`. */
export type TConversationMembershipStatus = 'active' | 'left';

/**
 * Sent by `fan_conversation/:id` and `messages_of_thread` (inside `community_details`)
 * for a cohort chat viewed by a member; `null` for creators, public and non-cohort chats.
 */
export interface IConversationViewerMembership {
  status: TConversationMembershipStatus;
  left_at: string | null;
  read_cutoff_message_id: string | null;
}

/** 403 code for a send or reaction from a former member of a cohort chat. */
export const CONVERSATION_READ_ONLY_CODE = 'conversation_read_only';

export const READ_ONLY_MEMBER_NOTICE =
  "You're no longer part of this group. You can read the history, but you can't send messages.";

export const FORMER_MEMBER_LABEL = 'Former member';

interface IMembershipSources {
  /** The open conversation; a thread response for another conversation is ignored. */
  conversationId?: string | null;
  /** `community_details` of the open thread's `messages_of_thread` response. */
  threadCommunityDetails?: {
    conversation_id?: string;
    viewer_membership?: IConversationViewerMembership | null;
  } | null;
  /** `viewer_membership` of the `fan_conversation/:id` response. */
  viewerMembership?: IConversationViewerMembership | null;
  /** `membership_status` of the inbox row the chat was opened from. */
  membershipStatus?: TConversationMembershipStatus | null;
}

/**
 * The viewer's membership of a conversation, from the freshest source that carries it:
 * the thread response, then the conversation detail, then the inbox row. A source whose
 * field is `null` is authoritative ("not a membership-tracked chat"); one without the
 * field (an older response, a row hydrated offline) defers to the next source.
 */
export function resolveConversationMembershipStatus({
  conversationId,
  threadCommunityDetails,
  viewerMembership,
  membershipStatus,
}: IMembershipSources): TConversationMembershipStatus | undefined {
  const threadMatches =
    !!threadCommunityDetails &&
    (!conversationId ||
      !threadCommunityDetails.conversation_id ||
      String(threadCommunityDetails.conversation_id) === String(conversationId));
  if (threadMatches && threadCommunityDetails.viewer_membership !== undefined) {
    return threadCommunityDetails.viewer_membership?.status ?? undefined;
  }
  if (viewerMembership !== undefined) {
    return viewerMembership?.status ?? undefined;
  }
  return membershipStatus ?? undefined;
}

export const isReadOnlyConversationMember = (sources: IMembershipSources): boolean =>
  resolveConversationMembershipStatus(sources) === 'left';

/**
 * A cohort chat: a private message group whose audience is a cohort (created from the
 * "From Cohorts" tab, or linked to one), not a community or topic. Only these can follow
 * their cohort ("Add people who join this cohort later").
 */
export const isCohortChatConversation = (conversation?: {
  conversation_type?: string | null;
  associated_cohort?: string | null;
  miscellaneous?: { from?: string } | null;
}): boolean => {
  if (!conversation) return false;
  const type = conversation.conversation_type;
  if (type === 'community' || type === 'topic') return false;
  return conversation.miscellaneous?.from === 'cohort' || !!conversation.associated_cohort;
};

/** DMs and member group chats: removal there still drops the chat (no read-only state). */
export const isPersonalConversationType = (type?: string | null): boolean =>
  type === 'direct_messages' || type === 'group_chat';

interface ICachedConversation {
  id?: string;
  conversation_type?: string | null;
  main_thread?: string;
}
type TCachedQuery = {
  data?: {
    conversation?: ICachedConversation;
    conversations?: {
      entities?: Record<string, { conversation?: ICachedConversation } | undefined>;
    };
  };
};

/**
 * A conversation as any cached RTK query holds it (a detail response or a normalized
 * list), or `undefined` when no cache has seen it. Pass the API slice's `queries`.
 */
export function findCachedConversation(
  queries: Record<string, TCachedQuery | undefined> | undefined,
  conversationId: string,
): ICachedConversation | undefined {
  for (const query of Object.values(queries ?? {})) {
    const data = query?.data;
    if (!data) continue;
    if (data.conversation && String(data.conversation.id) === String(conversationId)) {
      return data.conversation;
    }
    const row = data.conversations?.entities?.[conversationId];
    if (row?.conversation) return row.conversation;
  }
  return undefined;
}

/**
 * Whether a `conversation_participants_updated` event that names the viewer is about a
 * chat that keeps former members read-only (a cohort chat), so it should be refetched
 * rather than dropped. DMs and group chats are not: a removal there is a hard delete.
 * An unknown conversation (no type on the event, no cache has seen it) is not either,
 * which keeps the old drop path: a no-op for caches that never held it. A `null` type
 * (a cohort chat created without `2-way-messages`) counts as known.
 */
export function isMembershipTrackedConversation(
  eventConversationType: string | null | undefined,
  cached: ICachedConversation | undefined,
): boolean {
  const type =
    eventConversationType !== undefined
      ? eventConversationType
      : cached
      ? cached.conversation_type ?? null
      : undefined;
  return type !== undefined && !isPersonalConversationType(type);
}
